/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { Button, FormControlLabel, Checkbox, Tooltip, InputAdornment, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import FormTextField from "../../../../components/FormTextField/FormTextField";
import FormSelectField from "../../../../components/FormSelectField/FormSelectField";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import { BUILT_IN_TABS, TAB_TYPE_LABELS, TAB_TYPES, TAB_VARIANTS } from "../../../../utility/constants";
import { v4 as uuid } from "uuid";

// initialize a new tab config object
const newTabConfig = () => ({
	id: uuid(),
	displayName: "",
	description: "",
	queues: [],
	sortOrder: -1,
	variant: TAB_VARIANTS.dynamic,
	type: TAB_TYPES.attribute,
	hidden: false,
	config: {},
});

/**
 * Component that manages a tab layout configuration
 * @param {Object} param
 * - tab: tab config item
 * - availableBuiltInTabs: An object containing the available built in tabs.
 * - removeTabConfig: remove function
 * - setTabConfig: update function
 */
export const TabLayoutConfig = ({
	tab,
	availableBuiltInTabs,
	removeTabConfig,
	setTabConfig,
	formEdited,
	setFormEdited,
	queues,
}) => {
	const updatePropertyHandler = React.useCallback(
		(newValues) => setTabConfig({ ...tab, ...newValues }),
		[tab, setTabConfig],
	);
	const fieldStyle = {
		margin: "5px 10px",
	};

	// built-in read-only external tab was removed, but some customers might have it enabled
	// to maintain backwards compatibility we want to make it editable for the customer
	const disabled = !!BUILT_IN_TABS[tab.id];

	let tabTypeFields = null;
	let displayNameField = (
		<FormTextField
			label={"Display Name"}
			formEdited={formEdited}
			required
			errorMessage={"Display name is required."}
			setEdited={setFormEdited}
			startingValue={tab.displayName}
			setConfigValue={(valueName, value) => updatePropertyHandler({ [valueName]: value })}
			valueName={"displayName"}
			toolTipMessage="Label to display as the tab title."
			enableDebounce
			styleObject={{ ...fieldStyle, minWidth: 120 }}
			disabled={disabled}
		/>
	);
	let staticCheckBoxField = (
		<FormControlLabel
			value="top"
			style={{ display: "flex", alignItems: "center", margin: "-4px 10px 0 0" }}
			disabled={tab.hidden || tab.config?.canStatic === false}
			control={
				<Checkbox
					checked={tab.variant === TAB_VARIANTS.static}
					color="secondary"
					size="small"
					onChange={(event) => {
						updatePropertyHandler({ variant: event.target.checked ? TAB_VARIANTS.static : TAB_VARIANTS.dynamic });
					}}
				/>
			}
			label={
				<span style={{ fontSize: 14, marginLeft: -6 }}>
					<span>Static</span>
					<Tooltip
						arrow
						interactive
						placement={"bottom-start"}
						title="If a tab is static, it will appear on the agent console for all queues, even when an agent is not connected to a contact."
					>
						<i style={{ height: "15px", marginLeft: "4px", color: "gray" }} className="fas fa-info-circle" />
					</Tooltip>
				</span>
			}
		/>
	);
	if (BUILT_IN_TABS[tab.id]) {
		const options = [
			...Object.values(BUILT_IN_TABS).map((t) => ({
				id: t.id,
				label: t.displayName,
				disabled: t.id !== tab.id && !availableBuiltInTabs[t.id],
			})),
		].sort((a, b) => (a.label > b.label ? 1 : -1));
		displayNameField = (
			<FormSelectField
				label={"Display Name"}
				formEdited={formEdited}
				setEdited={setFormEdited}
				startingValue={BUILT_IN_TABS[tab.id]?.id || options[0].id}
				selectOptions={options}
				setConfigValue={(_property, value) => setTabConfig({ ...BUILT_IN_TABS[value] })}
				valueName={"type"}
				toolTipMessage="The built-in tab name. Each built-in tab has a different function. Read the description of a built in tab for further explanation of what each built in tab does."
				styleObject={{ ...fieldStyle, minWidth: 210 }}
				enableDebounce
			/>
		);
	} else if (tab.type === TAB_TYPES.attribute) {
		tabTypeFields = (
			<>
				<FormTextField
					label={"Attribute Prefix"}
					formEdited={formEdited}
					required
					errorMessage={"Attribute prefix is required."}
					setEdited={setFormEdited}
					startingValue={tab.config.attributePrefix || ""}
					setConfigValue={(property, value) => {
						setTabConfig({ ...tab, config: { ...tab.config, [property]: value ? value.toLowerCase() : "" } });
					}}
					valueName={"attributePrefix"}
					toolTipMessage="Lowercase tab prefix to use for grouping attributes for this tab."
					enableDebounce
					styleObject={{ ...fieldStyle, width: "67%" }}
					disabled={disabled}
				/>
			</>
		);
		//We do not want attribute tabs to be able to be static so we remove the static checkbox field.
		staticCheckBoxField = null;
	} else if (tab.type === TAB_TYPES.embed) {
		tabTypeFields = (
			<>
				<FormTextField
					label={"Web page URL"}
					formEdited={formEdited}
					required
					errorMessage={"Web page URL is required."}
					setEdited={setFormEdited}
					startingValue={tab.config.url || ""}
					setConfigValue={(property, value) => {
						setTabConfig({ ...tab, config: { ...tab.config, [property]: value } });
					}}
					valueName={"url"}
					toolTipMessage="URL of the web page to embed for this tab."
					enableDebounce
					styleObject={{ ...fieldStyle, width: "67%" }}
					disabled={disabled}
				/>
			</>
		);
	}
	return (
		<div className="d-flex flex-column" style={{ opacity: 0.9 }}>
			<div className="d-flex flex-row justify-content-between">
				<div className="d-flex" style={{ flex: 1 }}>
					<FormSelectField
						label={"Tab Type"}
						formEdited={formEdited}
						setEdited={setFormEdited}
						startingValue={BUILT_IN_TABS[tab.id] ? "builtIn" : tab.type}
						selectOptions={Object.keys(TAB_TYPE_LABELS).map((t) => ({
							id: t,
							label: TAB_TYPE_LABELS[t],
							disabled:
								TAB_TYPE_LABELS[t] === TAB_TYPE_LABELS.builtIn && Object.keys(availableBuiltInTabs).length === 0,
						}))}
						setConfigValue={(property, value) => {
							if (TAB_TYPES[value] === TAB_TYPES.builtIn) {
								setTabConfig({
									...Object.values(availableBuiltInTabs).sort((a, b) => (a.displayName > b.displayName ? 1 : -1))[0],
								});
							} else if (BUILT_IN_TABS[tab.id]) {
								setTabConfig(newTabConfig());
							} else {
								updatePropertyHandler({
									[property]: value,
									variant: value === TAB_TYPES.attribute ? TAB_VARIANTS.dynamic : tab.variant,
								});
							}
						}}
						valueName={"type"}
						toolTipMessage="Type of tab. Tab types can have different configuration options."
						styleObject={{ ...fieldStyle, minWidth: 120 }}
						enableDebounce
					/>
					{displayNameField}
				</div>
				<div
					style={{
						width: 300,
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "flex-end",
					}}
				>
					{staticCheckBoxField}
					<FormControlLabel
						value="top"
						style={{ display: "flex", alignItems: "center", margin: "-4px 0 0 0" }}
						control={
							<Checkbox
								checked={tab.hidden}
								color="secondary"
								size="small"
								onChange={(event) => {
									updatePropertyHandler({ hidden: event.target.checked });
								}}
							/>
						}
						label={
							<span style={{ fontSize: 14, marginLeft: -6 }}>
								<span>Hidden</span>
								<Tooltip
									arrow
									interactive
									placement={"bottom-start"}
									title="If a tab is hidden it will not show up in the agent console."
								>
									<i style={{ height: "15px", marginLeft: "4px", color: "gray" }} className="fas fa-info-circle" />
								</Tooltip>
							</span>
						}
					/>
					<div className="ml-3">
						<Button size="small" variant="outlined" style={{ color: "#cc1f20" }} onClick={removeTabConfig}>
							Remove
						</Button>
					</div>
				</div>
			</div>
			<div style={{ margin: "10px 10px 5px 5px" }}>
				<Autocomplete
					multiple
					fullWidth
					disabled={tab.variant === TAB_VARIANTS.static || tab.config?.limitQueueVisibility === false}
					disableCloseOnSelect
					options={queues}
					getOptionSelected={(queue, value) => queue.Name === value.Name}
					getOptionLabel={(queue) => queue.Name}
					defaultValue={tab.queues}
					value={tab.queues || []}
					onChange={(_event, newValue) => {
						const edited = tab.queues !== newValue;
						if (edited)
							updatePropertyHandler({
								queues: newValue,
							});
					}}
					renderOption={(option, { selected }) => (
						<React.Fragment>
							<Checkbox
								icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
								checkedIcon={<CheckBoxIcon fontSize="small" />}
								style={{ marginRight: 8 }}
								checked={selected}
							/>
							{option.Name}
						</React.Fragment>
					)}
					renderInput={(params) => (
						<TextField
							{...params}
							label={"Limit Visibility to Queues"}
							style={{ margin: "5px", minWidth: "40%" }}
							InputLabelProps={{
								shrink: true,
							}}
							InputProps={{
								...params.InputProps,
								style: {
									minHeight: 32,
								},
								startAdornment: (
									<>
										<InputAdornment position="start">
											<Tooltip
												arrow
												interactive
												placement={"bottom-start"}
												title="Select the queues for which this tab should be shown. When no values are selected, the tab will be visible for all queues."
											>
												<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
											</Tooltip>
										</InputAdornment>
										{params.InputProps.startAdornment}
									</>
								),
							}}
						/>
					)}
				/>
			</div>
			<div className="d-flex flex-row" style={{ margin: "5px 0 8px 0" }}>
				{tabTypeFields}
				<FormTextField
					disabled={disabled}
					label={"Description"}
					formEdited={formEdited}
					setEdited={setFormEdited}
					startingValue={tab.description}
					setConfigValue={(valueName, value) => updatePropertyHandler({ [valueName]: value })}
					valueName={"description"}
					toolTipMessage="Tab description that will be displayed on hover."
					enableDebounce
					styleObject={{ ...fieldStyle, width: "100%" }}
				/>
			</div>
		</div>
	);
};
